import { Config } from "../cfg/Config";

export type WeatherForecastDay = {
    date: string,
    weatherCode: number,
    tempMax: number,
    tempMin: number,
    precipitationProbability: number,
};

export type CurrentWeather = {
    temperature: number,
    weatherCode: number,
};

export type WeatherForecast = {
    current: CurrentWeather | null,
    daily: WeatherForecastDay[],
};

class WeatherService {
    // Default to Hanoi coordinates. Adjust as needed.
    private readonly latitude = 21.0294498;
    private readonly longitude = 105.8544441;
    private readonly apiKey = Config.OPENWEATHER_API_KEY;

    async getForecast(days: number = 5): Promise<WeatherForecast> {
        try {
            return await this.getForecastFromOpenWeather(days);
        } catch {
            // Fallback keeps weather features available when key is invalid or provider is down.
            return await this.getForecastFromOpenMeteo(days);
        }
    }

    private async getForecastFromOpenWeather(days: number = 5): Promise<WeatherForecast> {
        const forecastDays = Math.max(1, Math.min(days, 5));
        const currentUrl = `https://api.openweathermap.org/data/2.5/weather?lat=${this.latitude}&lon=${this.longitude}&appid=${this.apiKey}&units=metric`;
        const forecastUrl = `https://api.openweathermap.org/data/2.5/forecast?lat=${this.latitude}&lon=${this.longitude}&appid=${this.apiKey}&units=metric`;

        const [currentResponse, forecastResponse] = await Promise.all([
            fetch(currentUrl),
            fetch(forecastUrl),
        ]);

        if (!currentResponse.ok || !forecastResponse.ok) {
            throw new Error(`Weather API request failed: ${currentResponse.status}/${forecastResponse.status}`);
        }

        const currentData = await currentResponse.json();
        const forecastData = await forecastResponse.json();

        const groupedByDate: Record<string, {
            tempMax: number,
            tempMin: number,
            popMax: number,
            weatherCode: number,
        }> = {};

        (forecastData?.list ?? []).forEach((item: any) => {
            const date = String(item?.dt_txt ?? '').slice(0, 10);
            if (!date) {
                return;
            }
            const tempMax = Number(item?.main?.temp_max ?? item?.main?.temp ?? 0);
            const tempMin = Number(item?.main?.temp_min ?? item?.main?.temp ?? 0);
            const pop = Number(item?.pop ?? 0) * 100;
            const weatherCode = Number(item?.weather?.[0]?.id ?? 0);

            if (!groupedByDate[date]) {
                groupedByDate[date] = {
                    tempMax,
                    tempMin,
                    popMax: pop,
                    weatherCode,
                };
                return;
            }

            groupedByDate[date].tempMax = Math.max(groupedByDate[date].tempMax, tempMax);
            groupedByDate[date].tempMin = Math.min(groupedByDate[date].tempMin, tempMin);
            groupedByDate[date].popMax = Math.max(groupedByDate[date].popMax, pop);
        });

        const today = new Date();
        const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        const daily: WeatherForecastDay[] = Object.keys(groupedByDate)
            .sort((a, b) => (a > b ? 1 : -1))
            .filter((date) => date >= todayKey)
            .slice(0, forecastDays)
            .map((date) => ({
                date,
                weatherCode: groupedByDate[date].weatherCode,
                tempMax: groupedByDate[date].tempMax,
                tempMin: groupedByDate[date].tempMin,
                precipitationProbability: groupedByDate[date].popMax,
            }));

        const current = currentData
            ? {
                temperature: Number(currentData?.main?.temp ?? 0),
                weatherCode: Number(currentData?.weather?.[0]?.id ?? 0),
            }
            : null;

        return { current, daily };
    }

    private async getForecastFromOpenMeteo(days: number = 5): Promise<WeatherForecast> {
        const forecastDays = Math.max(1, Math.min(days, 7));
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${this.latitude}&longitude=${this.longitude}&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max&current=temperature_2m,weather_code&timezone=auto&forecast_days=${forecastDays}`;

        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Fallback weather API failed: ${response.status}`);
        }

        const data = await response.json();

        const daily: WeatherForecastDay[] = (data?.daily?.time ?? []).map((date: string, index: number) => ({
            date,
            weatherCode: Number(data.daily.weather_code?.[index] ?? 0),
            tempMax: Number(data.daily.temperature_2m_max?.[index] ?? 0),
            tempMin: Number(data.daily.temperature_2m_min?.[index] ?? 0),
            precipitationProbability: Number(data.daily.precipitation_probability_max?.[index] ?? 0),
        }));

        const current = data?.current
            ? {
                temperature: Number(data.current.temperature_2m ?? 0),
                weatherCode: Number(data.current.weather_code ?? 0),
            }
            : null;

        return { current, daily };
    }

    getWeatherLabelKey(weatherCode: number): string {
        // Open-Meteo WMO weather codes.
        if (weatherCode === 0) return 'weather.sunny';
        if (weatherCode === 1 || weatherCode === 2) return 'weather.partlyCloudy';
        if (weatherCode === 3) return 'weather.cloudy';
        if (weatherCode === 45 || weatherCode === 48) return 'weather.fog';
        if ([51, 53, 55, 56, 57].includes(weatherCode)) return 'weather.drizzle';
        if ([61, 63, 65, 66, 67].includes(weatherCode)) return 'weather.rain';
        if ([71, 73, 75, 77].includes(weatherCode)) return 'weather.snow';
        if ([80, 81, 82].includes(weatherCode)) return 'weather.shower';
        if ([95, 96, 99].includes(weatherCode)) return 'weather.thunderstorm';

        // OpenWeather condition codes.
        if (weatherCode >= 200 && weatherCode < 300) return 'weather.thunderstorm';
        if (weatherCode >= 300 && weatherCode < 400) return 'weather.drizzle';
        if (weatherCode >= 500 && weatherCode < 600) return 'weather.rain';
        if (weatherCode >= 600 && weatherCode < 700) return 'weather.snow';
        if (weatherCode >= 700 && weatherCode < 800) return 'weather.fog';
        if (weatherCode === 800) return 'weather.sunny';
        if (weatherCode === 801 || weatherCode === 802) return 'weather.partlyCloudy';
        if (weatherCode === 803 || weatherCode === 804) return 'weather.cloudy';
        return 'weather.unknown';
    }

    getWeatherIconClass(weatherCode: number): string {
        // Open-Meteo WMO weather codes.
        if (weatherCode === 0) return 'mdi mdi-weather-sunny';
        if (weatherCode === 1 || weatherCode === 2) return 'mdi mdi-weather-partlycloudy';
        if (weatherCode === 3) return 'mdi mdi-weather-cloudy';
        if (weatherCode === 45 || weatherCode === 48) return 'mdi mdi-weather-fog';
        if ([51, 53, 55, 56, 57].includes(weatherCode)) return 'mdi mdi-weather-pouring';
        if ([61, 63, 65, 66, 67].includes(weatherCode)) return 'mdi mdi-weather-rainy';
        if ([71, 73, 75, 77].includes(weatherCode)) return 'mdi mdi-weather-snowy';
        if ([80, 81, 82].includes(weatherCode)) return 'mdi mdi-weather-rainy';
        if ([95, 96, 99].includes(weatherCode)) return 'mdi mdi-weather-lightning-rainy';

        // OpenWeather condition codes.
        if (weatherCode >= 200 && weatherCode < 300) return 'mdi mdi-weather-lightning-rainy';
        if (weatherCode >= 300 && weatherCode < 400) return 'mdi mdi-weather-pouring';
        if (weatherCode >= 500 && weatherCode < 600) return 'mdi mdi-weather-rainy';
        if (weatherCode >= 600 && weatherCode < 700) return 'mdi mdi-weather-snowy';
        if (weatherCode >= 700 && weatherCode < 800) return 'mdi mdi-weather-fog';
        if (weatherCode === 800) return 'mdi mdi-weather-sunny';
        if (weatherCode === 801 || weatherCode === 802) return 'mdi mdi-weather-partlycloudy';
        if (weatherCode === 803 || weatherCode === 804) return 'mdi mdi-weather-cloudy';
        return 'mdi mdi-weather-cloudy-alert';
    }
}

export default new WeatherService();
